/**
 * Everything in the app is Europe/Paris wall-clock time, stored naive: dates as
 * `YYYY-MM-DD`, datetimes as `YYYY-MM-DDTHH:MM`. Every computation below is
 * calendar arithmetic on those strings — no value ever passes through an epoch
 * timestamp, so a DST switch cannot shift a weekly 09:00 slot to 08:00 or 10:00.
 *
 * Un seul calendrier pour les deux côtés : `server/src/time.ts` et
 * `client/src/time.ts` ne sont plus que des façades là-dessus, et ne gardent que
 * leurs ponts `Intl` (l'heure de Paris, les formats du navigateur). Rien ici ne
 * touche au DOM ni à Node : ce module part aussi dans le service worker.
 */
export const APP_TIME_ZONE = 'Europe/Paris';
/** Shared with the route JSON schemas, hence plain-string patterns. */
export const DATE_PATTERN = '^\\d{4}-\\d{2}-\\d{2}$';
export const TIME_PATTERN = '^([01]\\d|2[0-3]):[0-5]\\d$';
export const DATE_TIME_PATTERN = '^\\d{4}-\\d{2}-\\d{2}T([01]\\d|2[0-3]):[0-5]\\d$';
export const MONTH_PATTERN = '^\\d{4}-(0[1-9]|1[0-2])$';
export const QUARTER_PATTERN = '^\\d{4}-T[1-4]$';
const DATE_RE = new RegExp(DATE_PATTERN);
const TIME_RE = new RegExp(TIME_PATTERN);
const DATE_TIME_RE = new RegExp(DATE_TIME_PATTERN);
const MONTH_RE = new RegExp(MONTH_PATTERN);
const QUARTER_RE = new RegExp(QUARTER_PATTERN);
function splitDate(date) {
    if (!DATE_RE.test(date))
        return null;
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7));
    const day = Number(date.slice(8, 10));
    if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month))
        return null;
    return { year, month, day };
}
function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
function daysInMonth(year, month) {
    if (month === 2)
        return isLeapYear(year) ? 29 : 28;
    return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}
function pad(value, width) {
    return String(value).padStart(width, '0');
}
function formatCivilDate({ year, month, day }) {
    return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}
/** Howard Hinnant's days-from-civil: a proleptic Gregorian day count, epoch 1970-01-01. */
function toDayNumber({ year, month, day }) {
    const y = year - (month <= 2 ? 1 : 0);
    const era = Math.floor(y / 400);
    const yoe = y - era * 400;
    const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
    const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
    return era * 146097 + doe - 719468;
}
function fromDayNumber(dayNumber) {
    const z = dayNumber + 719468;
    const era = Math.floor(z / 146097);
    const doe = z - era * 146097;
    const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
    const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
    const mp = Math.floor((5 * doy + 2) / 153);
    const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
    const month = mp + (mp < 10 ? 3 : -9);
    return { year: yoe + era * 400 + (month <= 2 ? 1 : 0), month, day };
}
// ----------------------------------------------------------------- validations
/** La forme *et* le calendrier : un 30 février n'est pas une date. */
export function isValidDate(date) {
    return splitDate(date) !== null;
}
export function isValidTime(time) {
    return TIME_RE.test(time);
}
export function isValidDateTime(dateTime) {
    return DATE_TIME_RE.test(dateTime) && isValidDate(dateOf(dateTime));
}
export function isValidMonth(month) {
    return MONTH_RE.test(month);
}
export function isValidQuarter(quarter) {
    return QUARTER_RE.test(quarter);
}
function parseDate(date) {
    const parsed = splitDate(date);
    if (!parsed)
        throw new RangeError(`Invalid date: ${date}`);
    return parsed;
}
// ------------------------------------------------------------------ les jours
export function addDays(date, days) {
    return formatCivilDate(fromDayNumber(toDayNumber(parseDate(date)) + days));
}
/** 0 = Sunday … 6 = Saturday, matching `Date.prototype.getDay()`. */
export function weekdayOf(date) {
    const dayNumber = toDayNumber(parseDate(date));
    return (((dayNumber + 4) % 7) + 7) % 7;
}
/** Days from `from` to `to`, negative when `to` is earlier. */
export function daysBetween(from, to) {
    return toDayNumber(parseDate(to)) - toDayNumber(parseDate(from));
}
/** Weeks run Monday to Sunday. */
export function startOfWeek(date) {
    return addDays(date, -((weekdayOf(date) + 6) % 7));
}
/**
 * The first day on or after `startDate` falling on `weekday` — where a série
 * actually begins, and therefore the day its preview shows.
 */
export function firstOccurrence(startDate, weekday) {
    return addDays(startDate, (((weekday - weekdayOf(startDate)) % 7) + 7) % 7);
}
// ------------------------------------------------------------------- les mois
export function monthOf(date) {
    return date.slice(0, 7);
}
export function addMonths(month, delta) {
    const index = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + delta;
    return `${pad(Math.floor(index / 12), 4)}-${pad((index % 12) + 1, 2)}`;
}
/** Half-open day bounds of a `YYYY-MM` month: the 1st, and the 1st of the next one. */
export function monthBounds(month) {
    const year = Number(month.slice(0, 4));
    const monthNumber = Number(month.slice(5, 7));
    const rolls = monthNumber === 12;
    return {
        from: `${month}-01`,
        until: formatCivilDate({ year: rolls ? year + 1 : year, month: rolls ? 1 : monthNumber + 1, day: 1 }),
    };
}
// ------------------------------------------------------- trimestres URSSAF
/**
 * Calendar quarters, `YYYY-Tn`: T1 = janvier–mars … T4 = octobre–décembre. The
 * format sorts lexicographically, like every other date string here, so the
 * navigator's bounds are plain comparisons.
 */
export function quarterOf(dateOrMonth) {
    return `${dateOrMonth.slice(0, 4)}-T${Math.ceil(Number(dateOrMonth.slice(5, 7)) / 3)}`;
}
/** The first and last months of a quarter — how the navigator squares its span off. */
export function quarterMonthRange(quarter) {
    const { year, index } = splitQuarter(quarter);
    return { first: `${year}-${pad(index * 3 - 2, 2)}`, last: `${year}-${pad(index * 3, 2)}` };
}
/** The first month of a quarter: where its cell starts on the navigator grid. */
export function firstMonthOfQuarter(quarter) {
    return quarterMonthRange(quarter).first;
}
function splitQuarter(quarter) {
    return { year: Number(quarter.slice(0, 4)), index: Number(quarter.slice(6, 7)) };
}
/** Half-open day bounds of a quarter: its first day, and the first day of the next. */
export function quarterBounds(quarter) {
    const { year, index } = splitQuarter(quarter);
    const firstMonth = index * 3 - 2;
    return {
        from: formatCivilDate({ year, month: firstMonth, day: 1 }),
        until: index === 4
            ? formatCivilDate({ year: year + 1, month: 1, day: 1 })
            : formatCivilDate({ year, month: firstMonth + 3, day: 1 }),
    };
}
export function nextQuarter(quarter) {
    const { year, index } = splitQuarter(quarter);
    return index === 4 ? `${year + 1}-T1` : `${year}-T${index + 1}`;
}
export function previousQuarter(quarter) {
    const { year, index } = splitQuarter(quarter);
    return index === 1 ? `${year - 1}-T4` : `${year}-T${index - 1}`;
}
/**
 * The URSSAF declaration deadline: the last day of the month following the quarter.
 * T3 → 31 octobre, T4 → 31 janvier de l'année suivante.
 */
export function quarterDeadline(quarter) {
    // `until` is already the 1st of the month that follows the quarter.
    const { until } = quarterBounds(quarter);
    const { year, month } = parseDate(until);
    return formatCivilDate({ year, month, day: daysInMonth(year, month) });
}
// ----------------------------------------------------------------- les heures
export function atTime(date, time) {
    return `${date}T${time}`;
}
export function dateOf(dateTime) {
    return dateTime.slice(0, 10);
}
export function timeOf(dateTime) {
    return dateTime.slice(11, 16);
}
export function minutesOfTime(time) {
    return Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
}
/** L'heure d'un nombre de minutes, bornée à la journée — une position sur une grille. */
export function timeFromMinutes(minutes) {
    const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)));
    return `${pad(Math.floor(clamped / 60), 2)}:${pad(clamped % 60, 2)}`;
}
/** Wall-clock arithmetic: carries into the next or previous day, never into an offset. */
export function addMinutes(dateTime, minutes) {
    const total = minutesOfTime(timeOf(dateTime)) + minutes;
    const dayShift = Math.floor(total / (24 * 60));
    const rest = total - dayShift * 24 * 60;
    return atTime(addDays(dateOf(dateTime), dayShift), `${pad(Math.floor(rest / 60), 2)}:${pad(rest % 60, 2)}`);
}
export function endOf(startsAt, durationMin) {
    return addMinutes(startsAt, durationMin);
}
/** Wall-clock difference in minutes between two `YYYY-MM-DDTHH:MM`. */
export function minutesBetween(from, to) {
    return (daysBetween(dateOf(from), dateOf(to)) * 24 * 60 +
        minutesOfTime(timeOf(to)) -
        minutesOfTime(timeOf(from)));
}
// ------------------------------------------------------------- formats courts
/** « 9 h 15 » — l'heure comme elle se dit, dans une phrase. */
export function formatFrenchTime(time) {
    return `${Number(time.slice(0, 2))} h ${time.slice(3, 5)}`;
}
/** « 14 h », « 8 h » — l'heure pleine, celle d'une phrase qui dit « vers 14 h ». */
export function formatFrenchHour(time) {
    return `${Number(time.slice(0, 2))} h`;
}
/** « 28/08/2026 », the format the client shows everywhere. */
export function formatFrenchDate(date) {
    return `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`;
}
const FRENCH_MONTHS = [
    'janvier',
    'février',
    'mars',
    'avril',
    'mai',
    'juin',
    'juillet',
    'août',
    'septembre',
    'octobre',
    'novembre',
    'décembre',
];
/**
 * « septembre 2026 » — how a note names the month it bills, on paper and on screen.
 * Une table, et non `Intl` : une note est un document légal gardé six ans, il ne
 * dépendra pas des données ICU de la machine qui l'imprime.
 */
export function formatFrenchMonth(month) {
    return `${FRENCH_MONTHS[Number(month.slice(5, 7)) - 1] ?? month.slice(5, 7)} ${month.slice(0, 4)}`;
}
/** « 45 min », « 1 h », « 1 h 30 » — the shape the app uses everywhere. */
export function formatDuration(minutes) {
    if (minutes < 60)
        return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0 ? `${hours} h` : `${hours} h ${pad(rest, 2)}`;
}
//# sourceMappingURL=time.js.map